/*    
<summary>
  This functional component "AddTreatmentManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../../contexts/store-provider";
import LoaderButton from "../../../shared-components/button/loader-button";
import { observer } from "mobx-react";
import { FormikProps, Formik, Form, ErrorMessage } from "formik";
import AddOptionTreatmentForm from "./add-option-treatment-form";
import { IAddSaleItemVM, ITreatmentWithOptionsTime } from "../../../models/response/ISMRResponse";
import { SMRAddTreatmentFormSchema } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import SlidingPanel from "react-sliding-side-panel";
import { formatMessage } from "../../../translations/format-message";
import { serviceTypeEnum } from "../../../constants/enums/service-type-enum";
import ConfirmPopupHOC from "../../../shared-components/popup/confirm/confirm-popup-hoc";
import ConfirmTreatmentAdditionPopup from "./confirm-treatment-addition";
import toast from "react-hot-toast";
import moment from "moment";

interface IProps {
  storeId: number;
  businessTypeId: number;
  openPanel: boolean;
  initialValue: any;
  formValues: any;
  saleItemList: IAddSaleItemVM[];
  setSaleItemList: React.Dispatch<React.SetStateAction<IAddSaleItemVM[]>>;
  submitHandler: (values: any) => void;
  modalClosed: () => void;
  showConfirmPopup: boolean;
  confirmPopupToggleHandler: () => void;
  initialServicesTimeValue: ITreatmentWithOptionsTime;
}

interface IOptionTime {
  OptionId: number;
  TimeValue: number | null;
}

interface IInitialServiceTime {
  TreatmentId: number;
  TreatmentTimeValue: number | null;
  Options: IOptionTime[];
}

interface ISelectedService {
  TreatmentId: number;
  OptionIds: number[];
}

interface ITempSelectedService {
  Name: string;
  FeeExcludingTax: number;
  FeeIncludingTax: number;
  Tax: number;
  TaxAmount: number;
  Time: number;
  TreatmentData: any;
  OptionIds: number[];
}

const AddSMRTreatmentForm = (props: IProps) => {
  const { storeId, openPanel, initialValue, formValues, saleItemList, setSaleItemList, 
    showConfirmPopup, submitHandler, modalClosed, confirmPopupToggleHandler, businessTypeId, initialServicesTimeValue } = props;
  const {
    masterManagementStore,
    categoryManagementStore,
    bookingManagementStore,
    smrStore,
  } = useStore();
  const { allCategoryState, GetAllCategoriesServiceReservation } =
    categoryManagementStore;
  const { getAccordionDataById, getTreatmentOptionMenuListState } =
    bookingManagementStore;
  const { getAllTaxOptions, GetAllTaxOptionService, inProgress, getAllTimeOptions, GetAllTimeService } =
    masterManagementStore;
  const {
    isStaffAvailableState,
    isStaffAvailable,
    VerifyStaffIsAvailableOrNotService,
    resetIsStaffAvailableState,
    resetIsStaffAvailable,
    salesRecordAppointmentDetails
  } = smrStore;
  const formikRef = useRef<FormikProps<any> | null>(null);

  const [selectedTreatments, setSelectedTreatments] = useState<number[]>([]);
  const [submittedFormValues, setSubmittedFormValues] = useState<any>(null);

  useEffect(() => {
    formikRef.current?.resetForm(); // Reset form
    if(businessTypeId > 0){
      GetAllTaxOptionService(businessTypeId);
      GetAllTimeService(businessTypeId);
      GetAllCategoriesServiceReservation(businessTypeId); // Fetch all categories
    }
  }, []);

  const getTaxValue = (taxId: number) => {
    const taxOption = getAllTaxOptions?.find((taxes) => taxes?.id == taxId);
    if (taxOption) {
      return parseInt(taxOption.value);
    } else {
      return 0;
    }
  };

  const totalDuration = (selectedServices: any[]) => {
    let totalTime = 0;
    // Get all sale items for the current staff
  const staffSaleItems = saleItemList.filter(item => item.StaffId == props.formValues.StaffId && item.ItemType === serviceTypeEnum.Treatment);

  // Add durations from staff's existing sale items
  staffSaleItems.forEach(item => {
    const matchingService = selectedServices.find(service => service?.TreatmentData?.Id === item.ItemId);
    if (matchingService) {
      totalTime += matchingService?.Time ?? 0;
      
      // Add time for selected options that match
      matchingService.OptionIds?.forEach((optionId: any) => {
        const option = matchingService?.TreatmentData?.optionVm?.find(
          (op: any) => op.OptionId === optionId
        );
        totalTime += option?.TimeValue ?? 0;
      });
    }
  });
   selectedServices.forEach(service => {
    const isServiceAlreadyAdded = saleItemList.some(item => item.ItemId == service?.TreatmentData?.Id);

    if (!isServiceAlreadyAdded) {
      totalTime += service?.Time ?? 0;

      service?.OptionIds?.forEach((optionId: any) => {
        const option = service?.TreatmentData?.optionVm?.find(
          (op: any) => op.OptionId === optionId
        );
        totalTime += option?.TimeValue ?? 0;
      });
    }
  });
    return totalTime;
  };

  const getTimeValue = (timeId: number) => {
    const timeOption = getAllTimeOptions?.find((time) => time?.id == timeId);
    if (timeOption) {
      return typeof timeOption.value === 'string' ? parseInt(timeOption.value) : timeOption.value; // Return time value
    } else {
      return 0; // Return null if not found
    }
  };

  useEffect(() => {
    if(isStaffAvailableState.success) {
      if (!isStaffAvailable.status)
        confirmPopupToggleHandler();
        else
        {
          handleSubmit();
        }
      resetIsStaffAvailableState();
    }
  },[isStaffAvailableState.success]);

  const validateOptionMandatoryForTreatment = (selectedServices: any[]) =>{
    const treatmentsMissingOptions: string[] = [];
    const allSelectedOptionIds = selectedServices.flatMap((service: any) => service.OptionIds || []);

    for (const service of selectedServices) {
      const treatmentData = getAccordionDataById(service.TreatmentId);
      if (!treatmentData) continue;

      if (treatmentData.OptionMenu === 0 && Array.isArray(treatmentData.optionVm) && treatmentData.optionVm.length > 0) {
        const treatmentOptionIds = treatmentData?.optionVm.map((opt:any) => opt.OptionId);
        const isOptionSelectedAnywhere = treatmentOptionIds.some((optId: number) =>
          allSelectedOptionIds.includes(optId)
        );

        if (!isOptionSelectedAnywhere) {
          treatmentsMissingOptions.push(treatmentData.TreatmentName);
        }
      }
    }

    if (treatmentsMissingOptions.length > 0) {
      toast.error(
        `${formatMessage('option_is_mandatory_for')}: ${treatmentsMissingOptions.join(', ')}`,
      );
      return false; // Prevent submit
    }
    return true;
  }

  const handleSubmit = (selectedServices: any = null) => {
    let tempServices = submittedFormValues ? {...submittedFormValues} : null;
    if(selectedServices){
      tempServices = {...selectedServices}
    }
    if (tempServices !== null) {
      const servicesArray: IAddSaleItemVM[] = [];
      tempServices.selectedServices?.forEach((service: any) => {
        const existingTreatment = initialValue.find(
          (item: any) => item.TreatmentId === service.TreatmentId
        );

        const treatmentData = getAccordionDataById(service.TreatmentId);
        if (!treatmentData) return;

        const selectedOptionIds = service.OptionIds || [];

        if (existingTreatment) {
          // Treatment already exists, check for option changes
          const existingOptions =
            saleItemList?.filter(
              (item: any) =>
                item.ItemType === serviceTypeEnum.Option &&
                item.ItemName.startsWith(`${service.TreatmentId}_`)
            ) || [];

          const existingOptionIds = existingOptions.map(
            (opt: any) => opt.ItemId
          );

          // Add newly selected options
          selectedOptionIds.forEach((id: number) => {
            if (!existingOptionIds.includes(id)) {
              const option = treatmentData.optionVm?.find(
                (op: any) => op.OptionId === id
              );
              if (!option) return;

              servicesArray.push({
                ItemType: serviceTypeEnum.Option,
                ItemId: option.OptionId,
                ItemName: `${treatmentData.Id}_${option.OptionName}`,
                Quantity: 1,
                Price: option.FeeIncludingTax.toString(),
                Tax: getTaxValue(option.TaxId),
                TaxAmount: option.TaxAmount,
                DiscountType: 0,
                DiscountCash: 0,
                DiscountPercent: 0,
                StaffId: parseInt(props?.formValues?.StaffId),
                IsNominationApplied: props?.formValues?.IsNominationApplied,
                TimeValue: option.TimeValue
              });
            }
          });

          // Remove deselected options
          existingOptions.forEach((opt: any) => {
            if (!selectedOptionIds.includes(opt.ItemId)) {
              const indexToRemove = saleItemList.findIndex(
                (item: any) =>
                  item.ItemId === opt.ItemId &&
                  item.ItemType === serviceTypeEnum.Option &&
                  item.ItemName.startsWith(`${service.TreatmentId}_`)
              );
              if (indexToRemove !== -1) {
                setSaleItemList((prev) => {
                  const updatedList = [...prev];
                  updatedList.splice(indexToRemove, 1);
                  return updatedList;
                });
                // saleItemList.splice(indexToRemove, 1); // or use a state update if needed
              }
            }
          });
        } else {
          // Add new treatment
          servicesArray.push({
            ItemType: serviceTypeEnum.Treatment,
            ItemId: service.TreatmentId,
            ItemName: treatmentData.TreatmentName,
            Quantity: 1,
            Price: treatmentData.FeeIncludingTax,
            Tax: getTaxValue(treatmentData.TaxId),
            TaxAmount: treatmentData.TaxAmount,
            DiscountType: 0,
            DiscountCash: 0,
            DiscountPercent: 0,
            StaffId: parseInt(props?.formValues?.StaffId),
            IsNominationApplied: props?.formValues?.IsNominationApplied,
            TimeValue: getTimeValue(treatmentData.Time)
          });

          // Add selected options
          selectedOptionIds.forEach((id: number) => {
            const option = treatmentData.optionVm?.find(
              (op: any) => op.OptionId === id
            );
            if (!option) return;

            servicesArray.push({
              ItemType: serviceTypeEnum.Option,
              ItemId: option.OptionId,
              ItemName: `${treatmentData.Id}_${option.OptionName}`,
              Quantity: 1,
              Price: option.FeeIncludingTax.toString(),
              TaxAmount: option.TaxAmount,
              Tax: getTaxValue(option.TaxId),
              DiscountType: 0,
              DiscountCash: 0,
              DiscountPercent: 0,
              StaffId: parseInt(props?.formValues?.StaffId),
              IsNominationApplied: props?.formValues?.IsNominationApplied,
              TimeValue: option.TimeValue
            });
          });
        }
      });

      if (servicesArray.length > 0) {
        submitHandler(servicesArray);
      }
      setSelectedTreatments([]);
      onModalClosed();
    }
  };

  const onModalClosed = () => {
    modalClosed();
    resetIsStaffAvailable();
    resetIsStaffAvailableState();
  }
  const checkIfStaffAvailable = (values: any) => {
    const selectedServices: ISelectedService[] = values?.selectedServices || [];
    const isValidatedSuccessfully = validateOptionMandatoryForTreatment(selectedServices);
    if (!isValidatedSuccessfully) return;

    // 🔹 Map selected services to enriched data
    const tempSelectedServices: ITempSelectedService[] = selectedServices
      .map((service) => {
        const TreatmentData = getAccordionDataById(Number(service.TreatmentId));
        if (!TreatmentData) return null;

        const timeOption = getAllTimeOptions?.find(
          (time: any) => time.id === TreatmentData.Time
        );
        const getTime = timeOption?.value ? Number(timeOption.value) : 0;

        return {
          Name: TreatmentData.TreatmentName,
          FeeExcludingTax: TreatmentData.FeeExcludingTax,
          FeeIncludingTax: TreatmentData.FeeIncludingTax,
          Tax: TreatmentData.TaxId,
          TaxAmount: TreatmentData.TaxAmount,
          Time: getTime,
          TreatmentData,
          OptionIds: service.OptionIds,
        };
      })
      .filter(Boolean) as ITempSelectedService[];

    setSubmittedFormValues(values);

    // Step 1: Base total time from selected services
    let totalMinutes = totalDuration(tempSelectedServices);

    // Step 2: Add missing treatment/option times from initialServicesTimeValue[]
    if (Array.isArray(initialServicesTimeValue)) {
      (initialServicesTimeValue as IInitialServiceTime[]).forEach((initialService) => {
        const { TreatmentId, TreatmentTimeValue, Options = [] } = initialService;

        //Check if this treatment is selected in tempSelectedServices
        const selectedTreatment = tempSelectedServices.find(
          (s) => s.TreatmentData?.Id === TreatmentId
        );

        //Gather all OptionIds across all selected treatments
        const allSelectedOptionIds = tempSelectedServices.flatMap(
          (s) => s.OptionIds || []
        );

        if (!selectedTreatment) {
          //Treatment not selected → add its time
          totalMinutes += TreatmentTimeValue ?? 0;

          // Add only options that aren't selected anywhere
          Options.forEach((opt) => {
            if (!allSelectedOptionIds.includes(opt.OptionId)) {
              totalMinutes += opt.TimeValue ?? 0;
            }
          });
        } else {
          //Treatment is selected → add only missing options (not selected globally)
          Options.forEach((opt) => {
            if (!allSelectedOptionIds.includes(opt.OptionId)) {
              totalMinutes += opt.TimeValue ?? 0;
            }
          });
        }
      });

    }

    //Step 3: Add post-break time if editing existing appointment
    if (salesRecordAppointmentDetails.AppointmentId > 0) {
      totalMinutes += salesRecordAppointmentDetails.PostBreakTime;
    }

    //Step 4: Validate midnight overflow
    const startDateTime = moment(
      `${formValues.Date} ${formValues.Time}`,
      "YYYY-MM-DD HH:mm"
    );
    const endDateTime = moment(startDateTime).add(totalMinutes, "minutes");

    if (!startDateTime.isSame(endDateTime, "day")) {
      toast.error(formatMessage("the_selected_service_time_exceeds_midnight"));
      return;
    }

    //Step 5: Build and call request
    const requestBody = {
      StoreId: storeId,
      AssignedStaffId: formValues.StaffId,
      Date: formValues.Date,
      Time: formValues.Time + ":00",
      TotalTime: totalMinutes,
    };
    if(totalMinutes > 0)
      VerifyStaffIsAvailableOrNotService(requestBody);
    else
      handleSubmit(values);
    };
  return (
    <>
      {showConfirmPopup && (
        <ConfirmTreatmentAdditionPopup
          errorMsg={isStaffAvailable.errorMsg}
          modalSubmit={()=>handleSubmit()}
          modalClosed={() => {
            confirmPopupToggleHandler();
            resetIsStaffAvailableState();
            onModalClosed();
          }}
          isLoading={false}
          title={formatMessage("confirmation_popup_title")}
          message={formatMessage("publish_unpublish_schedule_confirmation")}
        />
      )}
      <SlidingPanel
        type={"right"}
        isOpen={openPanel}
        noBackdrop={false}
        size={100}
      >
        <div className="bg_slide panel">
          <div className="row bb p-2 pl-4 g-0 align-items-center ">
            <div className="col-10">
              <h2 className="fw-bold mb-0 mt-1 py-2">
                <FormattedMessage id="add_treatment" />
              </h2>
            </div>
            <div className="col-2">
              <span title={formatMessage("close")}>
                <svg
                  onClick={onModalClosed}
                  role="button"
                  xlinkTitle="close"
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  fill="currentColor"
                  className="bi bi-x ms-auto d-block"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </span>
            </div>
          </div>
          <div>
            {(allCategoryState.inProgress ||
              inProgress ||
              getTreatmentOptionMenuListState.inProgress) && (
              <FormLoader
                loading={
                  allCategoryState.inProgress ||
                  inProgress ||
                  getTreatmentOptionMenuListState.inProgress
                }
              />
            )}
            <Formik
              initialValues={{
                selectedServices: initialValue,
              }}
              validationSchema={SMRAddTreatmentFormSchema} // Apply validation schema
              onSubmit={checkIfStaffAvailable} // Handle form submission
              innerRef={formikRef} // Reference to Formik instance
            >
              {(formikProps) => (
                <Form className="sidebar_booking">
                  <div className="group_detailsWrapper">
                    <AddOptionTreatmentForm
                      formikProps={formikProps}
                      addedTreatments={initialValue}
                      setSelectedTreatmentId={setSelectedTreatments}
                    />

                    {/* Error Message */}
                    <ErrorMessage
                      name="selectedServices"
                      component="div"
                      className="text-danger ms-2 mb-2"
                    />
                    
                  </div>
                  {/* Footer Buttons */}
                    <div className="modal-footer center-content">
                      <Button
                        variant="outline-danger"
                        type="button"
                        onClick={onModalClosed}
                        // disabled={getTreatmentManagementLoading}
                      >
                        <i className="bi bi-x-lg me-1"></i>
                        <FormattedMessage id="close" />
                      </Button>
                      <LoaderButton
                        type="submit"
                        iconName="bi-check-lg"
                        // disabled={!hasNewTreatmentsOrOptions(initialValue, formikProps.values.selectedServices)}
                        isLoading={isStaffAvailableState.inProgress}
                        text="add"
                      />
                    </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </SlidingPanel>
    </>
  );
};

export default ConfirmPopupHOC(observer(AddSMRTreatmentForm));
